import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { pxToRem } from 'utils/cssUnits';

import {
  placeUndoNote,
  type PlanningUndoNote as PlanningUndoNoteValue,
  type UndoNotePosition,
} from './usePlanningUndo';

interface PlanningUndoNoteProps {
  note: PlanningUndoNoteValue | null;
}

/**
 * The note after an undo or redo in Planning (OP-2539). It floats over the
 * page — beside the element that had focus, or at the top of the tasks area
 * when nothing had — so nothing moves when it comes and goes. Never a toast.
 * A neutral grey, not the yellow information fill, which is for alerts.
 * The anchor it keeps in the tasks area takes no space.
 */
const PlanningUndoNote: React.FC<PlanningUndoNoteProps> = ({ note }) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [fallback, setFallback] = useState<UndoNotePosition | null>(null);

  useLayoutEffect(() => {
    if (!note || note.position || !anchorRef.current) {
      setFallback(null);
      return;
    }
    const anchor = anchorRef.current.getBoundingClientRect();
    setFallback(placeUndoNote(anchor, window.innerWidth));
  }, [note]);

  const position = note?.position ?? fallback;

  return (
    <>
      <div ref={anchorRef} aria-hidden className="h-0" />
      {note &&
        position &&
        createPortal(
          <span
            role="status"
            className="planning-undo-note pointer-events-none fixed z-[70] w-max max-w-[18rem] rounded border border-gray-medium bg-secondary-light px-1.5 py-0.5 text-sm text-charcoal"
            // Runtime coordinates, consumed by .planning-undo-note in App.css
            style={
              {
                '--undo-note-top': `${pxToRem(position.top)}rem`,
                '--undo-note-left': `${pxToRem(position.left)}rem`,
              } as React.CSSProperties
            }
          >
            {note.text}
          </span>,
          document.body
        )}
    </>
  );
};

export default PlanningUndoNote;
